import React from 'react';
import { convertToLocationTime } from './weatherUtils.js';

const Current = ({ weather, getMaxRainChance, getMaxSnowChance, formatWeatherCondition }) => {
  if (!weather) return null;

  // Get corrected weather condition text based on cloudiness overrides
  const getCorrectedConditionText = (symbolOrCondition, cloudinessPercent) => {
    if (!symbolOrCondition) return 'Unknown';
    
    const originalCondition = symbolOrCondition;
    
    // Handle Foreca symbol codes first
    if (typeof symbolOrCondition === 'string' && (symbolOrCondition.startsWith('d') || symbolOrCondition.startsWith('n'))) {
      const code = symbolOrCondition.substring(1);
      
      if (code.length >= 3) {
        const cloudiness = parseInt(code.charAt(0)); // 0-6
        const precipRate = code.length > 1 ? parseInt(code.charAt(1)) : 0; // 0-4
        
        // If there's precipitation or thunder, don't override
        if (precipRate > 0) {
          return originalCondition;
        }
        
        // Apply cloudiness overrides for clear conditions
        if (cloudinessPercent !== null) {
          if (cloudiness === 0) { // Symbol says clear
            if (cloudinessPercent >= 15) {
              return 'Partly Cloudy';
            } else if (cloudinessPercent >= 7) {
              return 'Mainly Clear';
            }
          } else if (cloudiness === 1 && cloudinessPercent >= 15) { // Almost clear with high cloudiness
            return 'Partly Cloudy';
          }
        }
      }
      
      // Return original if no override needed
      return originalCondition;
    }
    
    // Handle text-based conditions
    const normalizedCondition = originalCondition.toLowerCase();
    
    // Check for cloudiness overrides on text-based conditions
    if (cloudinessPercent !== null) {
      if ((normalizedCondition.includes('clear') && !normalizedCondition.includes('partly')) ||
          normalizedCondition.includes('sunny')) {
        if (cloudinessPercent >= 15) {
          return 'Partly Cloudy';
        } else if (cloudinessPercent >= 7) {
          return 'Mainly Clear';
        }
      }
      else if (normalizedCondition.includes('mainly clear') || 
               normalizedCondition.includes('mostly clear') || 
               normalizedCondition.includes('almost clear')) {
        if (cloudinessPercent >= 15) {
          return 'Partly Cloudy';
        }
      }
    }
    
    // Return original condition if no override needed
    return originalCondition;
  };

  // Function to get current cloudiness from hourly data
  const getCurrentCloudiness = () => {
    if (weather.hourlyMasterTable && weather.hourlyMasterTable.length > 0) {
      return weather.hourlyMasterTable[0].cloudiness || null;
    }
    // Fallback to current weather cloudiness if available
    return weather.current.cloudiness || null;
  };

  // Function to get current AQI from hourly data - UPDATED TO MATCH DAILY FORECAST
  const getCurrentAQI = () => {
    if (!weather.hourly_aqi || !weather.hourly_aqi.forecast || weather.hourly_aqi.forecast.length === 0) {
      return { aqi: null, category: 'Good', color: '#10b981' };
    }

    // Get the first (current) AQI reading
    const currentAQI = weather.hourly_aqi.forecast[0];
    
    const aqi = currentAQI.AQI || 0;
    let category = 'Good';
    let color = '#10b981';

    // SIMPLIFIED AQI categories to match DailyForecast
    if (!aqi || aqi === 0) {
      category = 'Good';
      color = '#10b981';
    } else if (aqi < 100) {
      category = 'Good';
      color = '#10b981';
    } else if (aqi < 150) {
      category = 'OK';
      color = '#f59e0b';
    } else if (aqi < 200) {
      category = 'Poor';
      color = '#f97316';
    } else {
      category = 'Bad';
      color = '#ef4444';
    }

    return {
      aqi: aqi,
      category: category,
      color: color
    };
  };

  // Function to get humidity description based on dew point - UPDATED THRESHOLDS
  const getHumidityDescription = () => {
    // Get current dew point from first hourly entry since current doesn't have it
    let dewPointC = 0;
    
    if (weather.hourlyMasterTable && weather.hourlyMasterTable.length > 0) {
      const currentHour = weather.hourlyMasterTable[0];
      dewPointC = currentHour.dewPoint || currentHour.dew_point_c || currentHour.dewpoint || 0;
    }
    
    // Fallback: try to get from current weather data
    if (dewPointC === 0) {
      dewPointC = weather.current.dewPoint || 
                  weather.current.dew_point_c || 
                  weather.current.dewpoint || 
                  weather.current.dew_point || 0;
    }
    
    // Debug logging to see what we're getting
    console.log('Dew point debug:', {
      hourlyDewPoint: weather.hourlyMasterTable?.[0]?.dewPoint,
      currentDewPoint: weather.current.dewPoint,
      finalDewPointC: dewPointC,
      hourlyData: weather.hourlyMasterTable?.[0],
      currentData: weather.current
    });
    
    const dewPointF = (dewPointC * 9/5) + 32;
    
    console.log('Dew point calculation:', { dewPointC, dewPointF });
    
    // UPDATED: Changed thresholds to 68.5°F for "Humid" and 80°F for "Very Humid"
    if (dewPointF <= 68.5) {
      return { text: 'Not Humid', color: '#d1d5db' };
    } else if (dewPointF < 80) {
      return { text: 'Humid', color: '#c4b5fd' }; // Light purple
    } else {
      return { text: 'Very Humid', color: '#8b5cf6' }; // Darker purple
    }
  };

  // Function to format location display - UPDATED: USA shows state, non-USA shows only city and country
  const formatLocationDisplay = () => {
    const { name, admin_area, country } = weather.location;
    
    // Simple debug logging (can be removed later)
    console.log('Location data:', { name, admin_area, country });
    
    // Convert country codes and names to preferred display format
    const getCountryName = (countryInput) => {
      if (!countryInput) return '';
      
      const input = countryInput.toLowerCase();
      
      // Handle both country codes and full country names
      const countryMap = {
        // Country codes
        'us': 'USA',
        'gb': 'United Kingdom', 
        'ca': 'Canada',
        'au': 'Australia',
        'fr': 'France',
        'de': 'Germany',
        'it': 'Italy',
        'es': 'Spain',
        'jp': 'Japan',
        'cn': 'China',
        'in': 'India',
        'br': 'Brazil',
        'mx': 'Mexico',
        'ru': 'Russia',
        'za': 'South Africa',
        'eg': 'Egypt',
        'ng': 'Nigeria',
        'ar': 'Argentina',
        'cl': 'Chile',
        'pe': 'Peru',
        'co': 'Colombia',
        've': 'Venezuela',
        'ec': 'Ecuador',
        'bo': 'Bolivia',
        'uy': 'Uruguay',
        'py': 'Paraguay',
        'gf': 'French Guiana',
        'sr': 'Suriname',
        'gy': 'Guyana',
        // Full country names that need conversion
        'united states': 'USA',
        'united states of america': 'USA',
        'usa': 'USA'
      };
      
      return countryMap[input] || countryInput;
    };

    // Function to convert US state names to abbreviations
    const getStateAbbreviation = (stateName) => {
      const stateMap = {
        'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
        'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
        'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
        'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
        'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
        'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
        'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
        'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
        'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
        'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
        'District of Columbia': 'DC'
      };
      
      return stateMap[stateName] || stateName;
    };

    const countryName = getCountryName(country);
    
    // Check if this is a USA location
    const isUSA = country?.toLowerCase() === 'us' || 
                  country?.toLowerCase() === 'usa' || 
                  country?.toLowerCase() === 'united states';
    
    // Build location string
    let locationString = name || 'Unknown Location';
    
    // For USA: include state/region, For non-USA: skip admin_area and go straight to country
    if (isUSA && admin_area && admin_area.toString().trim() && admin_area !== '""' && admin_area !== 'null' && admin_area !== '\"\"') {
      const stateAbbrev = getStateAbbreviation(admin_area);
      locationString += `, ${stateAbbrev}`;
    }
    
    // Add country name
    if (countryName) {
      locationString += `, ${countryName}`;
    }
    
    return locationString;
  };

  const aqiData = getCurrentAQI();
  const humidityData = getHumidityDescription();
  const currentCloudiness = getCurrentCloudiness();

  return (
    <div style={{width: '100%', marginBottom: '2rem'}}>
      {/* Location - USING ENHANCED formatLocationDisplay() */}
      <div style={{textAlign: 'center', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem'}}>
        <h2 style={{fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.5rem', wordBreak: 'break-word', lineHeight: '1.3', margin: 0}}>
          {formatLocationDisplay()}
        </h2>
        <button
          onClick={() => {
            const shareUrl = `${window.location.origin}${window.location.pathname}?city=${encodeURIComponent(weather.location.name)}`;
            navigator.clipboard.writeText(shareUrl).then(() => {
              // Weather report link copied to clipboard
            });
          }}
          style={{
            background: 'transparent',
            border: '1px solid #374151',
            borderRadius: '0.375rem',
            padding: '0.5rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#6b7280';
            e.target.style.color = '#d1d5db';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = '#374151';
            e.target.style.color = '#9ca3af';
          }}
          title="Copy Weatherlink to Share"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"/>
            <circle cx="6" cy="12" r="3"/>
            <circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
        </button>
      </div>

      {/* Current Weather - UPDATED WITH CLOUDINESS OVERRIDE */}
      <div style={{textAlign: 'center', marginBottom: '1rem', borderBottom: '1px solid #374151', paddingBottom: '1rem'}}>
        <div style={{fontSize: '1.1rem', marginBottom: '0.5rem'}}>
          Current Weather: {weather.current.temp_c}°C / {weather.current.temp_f}°F
        </div>
        <div style={{fontSize: '1rem', color: '#d1d5db'}}>
          <div style={{fontWeight: 'bold', color: 'white', marginBottom: '0.25rem'}}>
            {(() => {
              // Get the corrected condition text with cloudiness override
              const originalCondition = weather.current.condition.text;
              const correctedCondition = getCorrectedConditionText(originalCondition, currentCloudiness);
              return formatWeatherCondition ? formatWeatherCondition(correctedCondition) : correctedCondition;
            })()}
          </div>
          <div>Wind {(() => {
            // Convert wind direction degrees to compass direction
            const getWindDirection = (degrees) => {
              if (!degrees) return 'N';
              const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
              return directions[Math.round(degrees / 22.5) % 16];
            };
            
            // Wind is now in MPH from API, no conversion needed
            const windSpeedMph = weather.current.wind_mph || 0; // Already in MPH
            const windSpeedKmh = Math.round(windSpeedMph * 1.609); // Convert MPH to km/h
            const windDir = getWindDirection(weather.current.wind_dir);
            
            return `${windDir} ${Math.round(windSpeedMph)} mph / ${windSpeedKmh} kmh`;
          })()}</div>
        </div>
        {/* Only show rain/snow line if there's a non-zero chance */}
        {(getMaxRainChance() > 0 || getMaxSnowChance() > 0) && (
          <div style={{fontSize: '1rem', color: '#d1d5db', marginTop: '0.25rem'}}>
            {getMaxRainChance() > 0 && (
              <span>24 hour chance of Rain <span style={{color: '#3b82f6'}}>{Math.round(getMaxRainChance())}%</span></span>
            )}
            {getMaxRainChance() > 0 && getMaxSnowChance() > 0 && <span> • </span>}
            {getMaxSnowChance() > 0 && (
              <span>24 hour chance of Snow <span style={{color: '#60a5fa'}}>{Math.round(getMaxSnowChance())}%</span></span>
            )}
          </div>
        )}
        <div style={{fontSize: '1rem', color: '#d1d5db', marginTop: '0.25rem'}}>
          UV Index <span style={{color: '#ffc0cb'}}>{weather.current.uvIndex || 0}</span> • Air Quality <span style={{color: aqiData.color}}>{aqiData.aqi ? `${aqiData.category} (${aqiData.aqi})` : aqiData.category}</span>
        </div>
        <div style={{fontSize: '1rem', color: '#d1d5db', marginTop: '0.25rem'}}>
          Feels like {(() => {
            const feelsLikeC = weather.current.feelsLikeTemp || weather.current.temp_c;
            const feelsLikeF = Math.round((feelsLikeC * 9/5) + 32);
            return `${Math.round(feelsLikeC)}°C / ${feelsLikeF}°F`;
          })()} • <span style={{color: humidityData.color}}>{humidityData.text}</span>
        </div>
        <div style={{fontSize: '0.9rem', color: '#d1d5db', marginTop: '0.5rem'}}>
          {(() => {
            // Get today's sunrise/sunset from daily data - check multiple possible field names
            const now = new Date();
            const locationToday = new Date(now.toLocaleString("en-US", {timeZone: weather.location.timezone})).toISOString().split('T')[0];
            const todayData = weather.forecast.forecastday.find(day => day.date === locationToday);
            
            if (todayData && todayData.day) {
              // Try different possible field names from Foreca API
              const sunrise = todayData.day.sunrise || todayData.day.rise || todayData.day.sunriseTime;
              const sunset = todayData.day.sunset || todayData.day.set || todayData.day.sunsetTime;
              
              if (sunrise && sunset) {
                // Handle simple time strings (like "06:00:14") from Foreca API
                const formatSimpleTime = (timeString) => {
                  if (!timeString) return '';
                  
                  // If it's already a simple time string like "06:00:14", just format it
                  if (timeString.includes(':') && !timeString.includes('T')) {
                    const [hours, minutes] = timeString.split(':');
                    const hour = parseInt(hours);
                    const period = hour >= 12 ? 'PM' : 'AM';
                    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                    return `${displayHour}:${minutes} ${period}`;
                  }
                  
                  // Otherwise try to use the convertToLocationTime function
                  return convertToLocationTime(timeString, weather.location.timezone);
                };
                
                const sunriseTime = formatSimpleTime(sunrise);
                const sunsetTime = formatSimpleTime(sunset);
                
                return (
                  <>
                    <span style={{color: '#fbbf24'}}>Sunrise {sunriseTime}</span> • <span style={{color: '#f97316'}}>Sunset {sunsetTime}</span>
                  </>
                );
              }
            }
            return null;
          })()}
        </div>
      </div>
    </div>
  );
};

export default Current;